import { PropsWithChildren, createContext, useContext, useState } from 'react';
import { useConfig } from 'providers/config/ConfigProvider';
import { createAuthMethod } from 'auth/auth-methods/auth-method';
import { mapUserAndTokenToAuthenticatedUser } from 'auth/mappers/store-to-authenticated-user';
import {
  getInitialIdentificationState,
  getInitialSignedInUser,
  getInitialToken
} from './auth-provider.function';
import { useAuthentication } from './useAuthentication';
import { useSessionRefresh } from './useSessionRefresh';
import { IdentificationState } from '@sanlam-glacier/app-common/types/auth/identification-state.type.js';
import { UserInfo } from '@sanlam-glacier/app-common/types/auth/user-info.type.js';
import { TokenResponse } from '@sanlam-glacier/app-common/types/auth/token-response.type.js';
import { AuthenticatedUser } from '@sanlam-glacier/app-common/types/auth/authenticated-user.type.js';
import { AuthNavigate } from '@sanlam-glacier/app-common/types/auth/auth-navigate.type.js';
import { useUserContext } from 'user-context-manager/user-context-manager';
import { AuthenticationMethod } from 'auth/auth-methods/authentication-method.type';

const AuthContext = createContext<AuthenticationMethod | null>(null);

export const useAuth = () => {
  const authContext = useContext(AuthContext);
  if (!authContext) throw new Error('Cannot use AuthContext outside of an AuthProvider');
  return authContext;
};

export const AuthProvider = ({ children }: PropsWithChildren) => {
  const env = useConfig();
  const { setCurrentAuthenticatedUser } = useUserContext();
  const [identificationState, setIdentificationState] = useState<IdentificationState>(
    getInitialIdentificationState(env)
  );
  const [signedInUser, setSignedInUser] = useState<UserInfo | undefined>(getInitialSignedInUser());
  const [token, setToken] = useState<TokenResponse | undefined>(getInitialToken());
  const getSignedInUser = (): AuthenticatedUser | undefined =>
    mapUserAndTokenToAuthenticatedUser(signedInUser, token, env);

  const navigate: AuthNavigate = (to: string) => {
    const isAbsolute = to.indexOf('://') > 0 || to.indexOf('//') === 0;
    if (isAbsolute) {
      window.location.assign(to);
      return;
    }

    const base = env.routerBasePath;
    let destination = to;
    if (base) {
      destination = `${base}/${destination}`;
      destination = destination.replaceAll(/\/+/g, '/');
    }

    if (window.location.pathname === destination) return;
    window.location.assign(destination);
  };

  const authApi = createAuthMethod({
    method: env.auth.method,
    env,
    setSignedInUser,
    identificationState: identificationState,
    getSignedInUser,
    setToken,
    setCurrentAuthenticatedUser,
    navigate
  });

  const { isError, isAuthLoading } = useAuthentication({
    token,
    signedInUser,
    authApi,
    identificationState,
    setIdentificationState
  });

  useSessionRefresh({ authApi, token });

  return (
    <AuthContext.Provider value={authApi}>
      {!isError && !isAuthLoading && children}
    </AuthContext.Provider>
  );
};
